import { Check } from 'lucide-react';
import styles from '../styles/AIAssistant.module.css';
import { Select, Text } from '../ui/primitives.js';

export type ModelSelectorOption<T extends string = string> = {
  value: T;
  label: string;
  badge?: string;
  description?: string;
};

export type AIAssistantModelSelectorProps<T extends string = string> = {
  options: ModelSelectorOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  /** Label above the select. Defaults to "Model". */
  label?: string;
  /** Render a compact button list for popover menus instead of a nested select. */
  variant?: 'select' | 'menu';
  testId?: string;
  menuTestId?: string;
  optionTestIdPrefix?: string;
};

export function AIAssistantModelSelector<T extends string = string>({
  options,
  value,
  onChange,
  disabled = false,
  label = 'Model',
  variant = 'select',
  testId = 'ai-model-selector',
  menuTestId,
  optionTestIdPrefix = 'ai-model-option',
}: AIAssistantModelSelectorProps<T>) {
  const handleChange = (next: string | null) => {
    if (next != null) {
      onChange(next as T);
    }
  };

  if (variant === 'menu') {
    return (
      <div
        className={styles.modelMenuSelector}
        data-testid={testId}
        data-ai-model-selector="menu"
      >
        <div
          className={styles.modelMenuSurface}
          data-testid={menuTestId}
        >
          <Text className={styles.modelMenuLabel}>{label}</Text>
          <div className={styles.modelMenuOptions} role="listbox" aria-label={label}>
          {options.map((option) => {
            const selected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                className={`${styles.modelMenuOption} ${selected ? styles.modelMenuOptionSelected : ''}`}
                data-ai-model-option="true"
                data-testid={`${optionTestIdPrefix}-${option.value}`}
                data-selected={selected ? 'true' : 'false'}
                role="option"
                aria-selected={selected}
                disabled={disabled}
                onClick={() => onChange(option.value)}
              >
                <span className={styles.modelMenuOptionBody}>
                  <span className={styles.modelMenuOptionMain}>
                    <span className={styles.modelMenuOptionLabel}>{option.label}</span>
                    {option.badge ? (
                      <span className={styles.modelMenuOptionBadge}>{option.badge}</span>
                    ) : null}
                  </span>
                  {option.description ? (
                    <span className={styles.modelMenuOptionDescription}>{option.description}</span>
                  ) : null}
                </span>
                <span
                  className={styles.modelMenuOptionCheck}
                  aria-hidden="true"
                  data-testid={`${optionTestIdPrefix}-${option.value}-check`}
                >
                  {selected ? <Check size={14} strokeWidth={2.5} /> : null}
                </span>
              </button>
            );
          })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modelSelector} data-testid={testId}>
      <Text className={styles.modelLabel}>{label}</Text>
      <Select
        data={options}
        value={value}
        onChange={handleChange}
        size="xs"
        radius="md"
        variant="filled"
        allowDeselect={false}
        className={styles.modelSelect}
        comboboxProps={{ withinPortal: true }}
        data-testid="ai-model-select"
        aria-label={label}
        disabled={disabled}
      />
    </div>
  );
}
